import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { setSentryUserContext } from "@/lib/sentry";

export type Rol = "admin_central" | "operador_tienda" | "contador";

export interface AuthUser {
  id: string;
  email: string | undefined;
  nombre_completo: string;
  rol: Rol;
  tienda_id: string | null;
  tienda_nombre?: string | null;
  tienda_tipo_producto?: "moto" | "celular" | "mixto" | null;
  onboarding_completado: boolean;
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function load() {
      const { data } = await supabase.auth.getUser();
      if (!data.user) {
        setLoading(false);
        return;
      }
      const { data: perfil } = await supabase
        .from("perfiles")
        .select("*, tiendas(nombre, tipo_producto)")
        .eq("id", data.user.id)
        .single();

      if (!mounted || !perfil) {
        setLoading(false);
        return;
      }

      const u: AuthUser = {
        id: data.user.id,
        email: data.user.email,
        nombre_completo: perfil.nombre_completo,
        rol: perfil.rol,
        tienda_id: perfil.tienda_id,
        tienda_nombre: perfil.tiendas?.nombre ?? null,
        tienda_tipo_producto: perfil.tiendas?.tipo_producto ?? null,
        onboarding_completado: perfil.onboarding_completado,
      };
      setUser(u);
      setSentryUserContext({
        id: u.id,
        email: u.email,
        rol: u.rol,
        tienda_id: u.tienda_id ?? undefined,
      });
      setLoading(false);
    }

    load();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setUser(null);
        setSentryUserContext(null);
      } else {
        load();
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { user, loading, isAdmin: user?.rol === "admin_central", isOperador: user?.rol === "operador_tienda" };
}
